import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';

function InvoiceDetail() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [markingPaid, setMarkingPaid] = useState(false);
  const [voiding, setVoiding] = useState(false);

  useEffect(() => {
    fetchInvoice();
  }, [invoiceId]);

  const fetchInvoice = async () => {
    setLoading(true);
    try {
      const data = await api.getInvoice(invoiceId);
      setInvoice(data);
    } catch (err) {
      setError(err.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (markingPaid) return;
    if (!window.confirm('Mark this invoice as paid?')) return;

    setMarkingPaid(true);
    try {
      const updated = await api.markInvoicePaid(invoiceId);
      setInvoice(updated);
    } catch (err) {
      alert(err.message || 'Failed to mark invoice as paid');
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleVoidInvoice = async () => {
    if (voiding) return;
    if (!window.confirm('Void this invoice? This cannot be undone. You will need to create a new invoice if needed.')) return;

    setVoiding(true);
    try {
      const updated = await api.voidInvoice(invoiceId);
      setInvoice(updated);
    } catch (err) {
      alert(err.message || 'Failed to void invoice');
    } finally {
      setVoiding(false);
    }
  };

  const handleEditInvoice = () => {
    if (invoice.status === 'sent') {
      if (window.confirm('This invoice was already sent. Editing may cause discrepancies with what the client received. Continue?')) {
        navigate(`/invoices/build/${invoice.job_id}`);
      }
    } else {
      navigate(`/invoices/build/${invoice.job_id}`);
    }
  };

  const formatCurrency = (amount, currency = '$') => {
    if (!amount) return `${currency}0.00`;
    return `${currency}${parseFloat(amount).toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      draft: 'bg-slate-100 text-slate-700',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      voided: 'bg-red-100 text-red-800',
    };
    return statusStyles[status] || 'bg-slate-100 text-slate-700';
  };

  const calculateTotal = () => {
    if (!invoice?.invoice_line_items?.length) return parseFloat(invoice?.total_with_tax || 0);
    return invoice.invoice_line_items.reduce((sum, item) => {
      if (item.type === 'labor' || item.type === 'hourly') {
        return sum + (parseFloat(item.hours || 0) * parseFloat(item.rate || 0));
      }
      return sum + parseFloat(item.cost || 0);
    }, 0);
  };

  if (loading) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-slate-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-xl text-center">
            <i className="fas fa-exclamation-circle text-3xl mb-3"></i>
            <p>{error}</p>
            <Link
              to="/invoices"
              className="mt-4 inline-block text-primary hover:underline"
            >
              Back to Invoices
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-600">Invoice not found</p>
          <Link to="/invoices" className="text-primary hover:underline">
            Back to Invoices
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 px-6 bg-background min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <Link
          to="/invoices"
          className="inline-flex items-center text-slate-600 hover:text-primary mb-6 transition"
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Back to Invoices
        </Link>

        {/* Header */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-primary mb-2">
                Invoice #{invoice.invoice_number || invoice.id?.slice(0, 8)}
              </h1>
              <span className={`inline-block px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadge(invoice.status)}`}>
                {invoice.status}
              </span>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Total Amount</p>
              <p className="text-3xl font-bold text-primary">
                {formatCurrency(invoice.total_with_tax || calculateTotal())}
              </p>
            </div>
          </div>

          {/* Invoice Meta */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-slate-200">
            {invoice.client_name && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Client</p>
                <p className="font-medium text-slate-800">
                  <i className="fas fa-user mr-2 text-slate-400"></i>
                  {invoice.client_name}
                </p>
              </div>
            )}
            {invoice.job_title && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Job</p>
                <p className="font-medium text-slate-800">
                  <i className="fas fa-briefcase mr-2 text-slate-400"></i>
                  {invoice.job_id && (
                    <Link to={`/jobs/${invoice.job_id}`} className="text-primary hover:underline">
                      {invoice.job_title}
                    </Link>
                  )}
                  {!invoice.job_id && invoice.job_title}
                </p>
              </div>
            )}
            {invoice.due_date && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Due Date</p>
                <p className="font-medium text-slate-800">
                  <i className="fas fa-calendar mr-2 text-slate-400"></i>
                  {new Date(invoice.due_date).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Line Items */}
        {invoice.invoice_line_items && invoice.invoice_line_items.length > 0 && (
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 mb-6">
            <h2 className="text-lg font-bold text-primary mb-4">Line Items</h2>
            <div className="space-y-3">
              {invoice.invoice_line_items.map((item, index) => {
                const isLabor = item.type === 'labor' || item.type === 'hourly';
                const itemTotal = isLabor
                  ? parseFloat(item.hours || 0) * parseFloat(item.rate || 0)
                  : parseFloat(item.cost || 0);

                return (
                  <div
                    key={item.id || index}
                    className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{item.description}</p>
                      <p className="text-sm text-slate-500">
                        {isLabor ? (
                          <>{item.hours}h @ {formatCurrency(item.rate)}/hr</>
                        ) : (
                          <span className="capitalize">{item.type || 'Material'}</span>
                        )}
                      </p>
                    </div>
                    <p className="font-semibold text-slate-800">
                      {formatCurrency(itemTotal)}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Total */}
            <div className="flex items-center justify-between pt-4 mt-4 border-t-2 border-slate-200">
              <p className="text-lg font-bold text-slate-800">Total</p>
              <p className="text-xl font-bold text-primary">
                {formatCurrency(invoice.total_with_tax || calculateTotal())}
              </p>
            </div>
          </div>
        )}

        {/* Voided Banner */}
        {invoice.status === 'voided' && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl mb-6 text-center">
            <i className="fas fa-ban mr-2"></i>
            This invoice has been voided and cannot be edited
          </div>
        )}

        {/* Actions */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
          <h2 className="text-lg font-bold text-primary mb-4">Actions</h2>
          <div className="flex flex-wrap gap-3">
            {/* View Job - always show if job exists */}
            {invoice.job_id && (
              <Link
                to={`/jobs/${invoice.job_id}`}
                className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition"
              >
                <i className="fas fa-briefcase mr-2"></i>
                View Job
              </Link>
            )}

            {/* Edit Invoice - hide if paid/voided, warn if sent */}
            {invoice.job_id && invoice.status !== 'paid' && invoice.status !== 'voided' && (
              <button
                onClick={handleEditInvoice}
                className="px-4 py-2 bg-secondary text-white rounded-lg font-medium hover:bg-secondary/90 transition"
              >
                <i className="fas fa-edit mr-2"></i>
                Edit Invoice
              </button>
            )}

            {/* Mark as Paid - only for draft/sent */}
            {invoice.status !== 'paid' && invoice.status !== 'voided' && (
              <button
                onClick={handleMarkPaid}
                disabled={markingPaid}
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition disabled:opacity-50"
              >
                {markingPaid ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Marking Paid...
                  </>
                ) : (
                  <>
                    <i className="fas fa-dollar-sign mr-2"></i>
                    Mark as Paid
                  </>
                )}
              </button>
            )}

            {/* Void Invoice - show for sent only (paid invoices cannot be voided) */}
            {invoice.status === 'sent' && (
              <button
                onClick={handleVoidInvoice}
                disabled={voiding}
                className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition disabled:opacity-50"
              >
                {voiding ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Voiding...
                  </>
                ) : (
                  <>
                    <i className="fas fa-ban mr-2"></i>
                    Void Invoice
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Timestamps */}
        <div className="mt-6 text-center text-sm text-slate-500">
          {invoice.created_at && (
            <p>Created: {new Date(invoice.created_at).toLocaleString()}</p>
          )}
          {invoice.sent_at && (
            <p>Sent: {new Date(invoice.sent_at).toLocaleString()}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default InvoiceDetail;
